import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Slider,
  TextField,
  Typography,
  useTheme
} from '@mui/material';
import { Candidate, Tabulation, TabulationTally } from '../../types';
import React, { useEffect, useState } from 'react';
import { db, functions } from '../../firebase';
import { doc, getDoc, serverTimestamp, updateDoc } from 'firebase/firestore';

import { httpsCallable } from 'firebase/functions';
import { styled } from '@mui/material/styles';

interface ProjectionDialogProps {
  open: boolean;
  region: string;
  province: string;
  district: string;
  municipal: string;
  barangay: string;
  position: string;
  candidates: Candidate[];
  tabulation: Tabulation;
  onClose: () => void;
}

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: theme.spacing(2),
    boxShadow: theme.shadows[10],
  }
}));

const ProjectionDialog: React.FC<ProjectionDialogProps> = ({ 
  open,
  region,
  province,
  district,
  municipal,
  barangay,
  position,
  candidates,
  tabulation,
  onClose
}) => {
  const handleClose = async () => {
    try {
      const projectionDocPath = `tabulationsV2/SURIGAO DEL NORTE_DISTRICT 1/regions/${region}/provinces/${province}/districts/${district}/cities/${municipal}/barangays/${barangay}`;
      const projectionDocRef = doc(db, projectionDocPath);
      const projectionDocSnap = await getDoc(projectionDocRef);

      let projectionData: TabulationTally = {};
      if (projectionDocSnap.exists()) {
        projectionData = projectionDocSnap.data()?.projection || {};
      }

      // Update the projection data with new votes
      projectionData[position] = candidates.reduce((acc, candidate, index) => {
        acc[candidate.name] = candidateVotes[index];
        return acc;
      }, {} as Record<string, number>);

      // Update the document with new projection data
      await updateDoc(projectionDocRef, {
        projection: projectionData,
        updatedAt: serverTimestamp()
      });

      // const refreshTabulationsFunc = httpsCallable(functions, 'refresh_tabulations', { timeout: 1000 * 60 * 5 });
      // const result = await refreshTabulationsFunc({
      //   dashboardId: 'SURIGAO DEL NORTE_DISTRICT 1'
      // });
      // console.log('Tabulations refreshed:', result);

      // Call the original onClose handler
      onClose();
    } catch (error) {
      console.error('Error updating projection data:', error);
      onClose(); // Still close the dialog even if update fails
    }
  };
  const theme = useTheme();
  const [candidateVotes, setCandidateVotes] = useState<number[]>(
    candidates.map(() => tabulation.totalVoters / candidates.length)
  );
  const [undecidedVotes, setUndecidedVotes] = useState(tabulation.totalVoters);

  useEffect(() => {
    const fetchProjectionData = async () => {
      try {
        const projectionDocPath = `tabulationsV2/SURIGAO DEL NORTE_DISTRICT 1/regions/${region}/provinces/${province}/districts/${district}/cities/${municipal}/barangays/${barangay}`;
        const projectionDocRef = doc(db, projectionDocPath);
        const projectionDocSnap = await getDoc(projectionDocRef);

        if (projectionDocSnap.exists()) {
          const tabulation = projectionDocSnap.data() as Tabulation;
          const projection = tabulation.projection as TabulationTally;
          if (projection) {
            const newCandidateVotes = candidates.map((candidate) => (
              projection[position][candidate.name] || 0
            ));
            setCandidateVotes(newCandidateVotes);
          }
        }
      } catch (error) {
        console.error('Error fetching projection data:', error);
      }
    };

    if (candidates.length > 0) {
      fetchProjectionData();
    }
  }, [region, province, district, municipal, barangay, candidates, position]);

  useEffect(() => {
    const totalAssignedVotes = candidateVotes.reduce((a, b) => a + b, 0);
    setUndecidedVotes(Math.max(0, tabulation.totalVoters - totalAssignedVotes));
  }, [candidateVotes, tabulation.totalVoters]);

  const handleVoteChange = (index: number, newVotes: number) => {
    const newCandidateVotes = [...candidateVotes];
    const currentTotal = newCandidateVotes.reduce((a, b) => a + b, 0);
    
    // Ensure the new value doesn't exceed total voters
    const cappedVotes = Math.min(newVotes, tabulation.totalVoters);
    
    // Calculate the difference from the current value
    const diff = cappedVotes - newCandidateVotes[index];
    
    // If the new total would exceed total voters, redistribute
    if (currentTotal + diff > tabulation.totalVoters) {
      // Calculate how much needs to be redistributed
      let excess = currentTotal + diff - tabulation.totalVoters;
      
      // Redistribute excess votes among other candidates
      newCandidateVotes[index] = cappedVotes - excess;
      
      // Distribute remaining excess to subsequent candidates
      for (let i = index + 1; i < newCandidateVotes.length; i++) {
        if (excess <= 0) break;
        
        const subtractAmount = Math.min(excess, newCandidateVotes[i]);
        newCandidateVotes[i] -= subtractAmount;
        excess -= subtractAmount;
      }
    } else {
      // Normal case: just set the votes
      newCandidateVotes[index] = cappedVotes;
    }
    
    setCandidateVotes(newCandidateVotes);
  };

  const handleSliderChange = (index: number, newValue: number | number[]) => {
    const value = Array.isArray(newValue) ? newValue[0] : newValue;
    handleVoteChange(index, value);
  };

  const handlePercentageChange = (index: number, value: string) => {
    const numValue = parseFloat(value) || 0;
    const votes = Math.round((numValue / 100) * tabulation.totalVoters);
    handleVoteChange(index, votes);
  };

  const handleVoteCountChange = (index: number, value: string) => {
    const numValue = parseInt(value) || 0;
    handleVoteChange(index, numValue);
  };

  return (
    <StyledDialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      TransitionProps={{
        timeout: 300,
        appear: true,
        enter: true,
        exit: true
      }}
    >
      <DialogTitle sx={{ 
        backgroundColor: theme.palette.primary.main, 
        color: theme.palette.primary.contrastText,
        py: 2,
        textAlign: 'center'
      }}>
        {position} Projection in {barangay}
      </DialogTitle>
      <DialogContent sx={{ p: 3 }}>
        {candidates.map((candidate, index) => {
          const percentage = Math.round((candidateVotes[index] / tabulation.totalVoters) * 100);
          return (
            <Box 
              key={candidate.name} 
              style={{ 
                marginBottom: theme.spacing(3), 
                padding: theme.spacing(2), 
                backgroundColor: theme.palette.background.paper,
              }}
            >
              <Typography 
                gutterBottom 
                variant="h6" 
                sx={{ 
                  fontWeight: 'bold', 
                  color: theme.palette.text.primary 
                }}
              >
                {candidate.name}
              </Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={8}>
                  <Slider
                    value={candidateVotes[index]}
                    onChange={(_, newValue) => handleSliderChange(index, newValue)}
                    min={0}
                    max={tabulation.totalVoters}
                    valueLabelDisplay="auto"
                    color="primary"
                    sx={{ 
                      height: 8,
                      '& .MuiSlider-thumb': {
                        width: 24,
                        height: 24,
                        boxShadow: theme.shadows[3]
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={2}>
                  <TextField
                    type="number"
                    value={percentage}
                    onChange={(e) => handlePercentageChange(index, e.target.value)}
                    inputProps={{
                      min: 0,
                      max: 100
                    }}
                    label="%"
                    fullWidth
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={2}>
                  <TextField
                    type="number"
                    value={candidateVotes[index]}
                    onChange={(e) => handleVoteCountChange(index, e.target.value)}
                    inputProps={{
                      min: 0,
                      max: tabulation.totalVoters
                    }}
                    label="Votes"
                    fullWidth
                    variant="outlined"
                  />
                </Grid>
              </Grid>
            </Box>
          );
        })}
        <Typography 
          variant="subtitle1" 
            sx={{ 
              color: theme.palette.text.secondary,
              ml: 2
            }}
        >
          Undecided Votes: {undecidedVotes} ({Math.round((undecidedVotes / tabulation.totalVoters) * 100)}%)
        </Typography>
      </DialogContent>
      <DialogActions sx={{ p: 2, justifyContent: 'center' }}>
        <Button 
          onClick={handleClose} 
          variant="contained" 
          color="primary"
          sx={{ 
            px: 4, 
            py: 1, 
            borderRadius: 2 
          }}
        >
          Close
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

export default ProjectionDialog;
